import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TextField, Button, Typography, Paper, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { apiFetch } from '../../utils/apiFetch';
import './global/global.css';

const withStyles = makeStyles(() => ({
    root: {
        padding: '20px',
        maxWidth: '100%',
        margin: '0',
        marginBottom: '40px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    adminButton: {
        background: 'linear-gradient(.75turn, #808080 0%, #404040 100%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(.75turn, #808080 0%, #404040 100%)',
            opacity: 0.9,
        },
    },
    error: {
        color: 'red',
        marginTop: '10px',
    },
    success: {
        color: '#1faa60',
        marginTop: '10px',
        textAlign: 'center',
    },
    tableContainer: {
        marginTop: '20px',
        maxWidth: '100%',
        overflowX: 'auto',
    },
    table: {
        minWidth: 650,
    },
    tableHeader: {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
    },
}));

const AdminUserManagement = () => {
    const classes = withStyles();
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [subscribedUsers, setSubscribedUsers] = useState([]);
    const [inactiveSubscriptions, setInactiveSubscriptions] = useState([]);
    const [invitationStatus, setInvitationStatus] = useState({});
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
    });

    useEffect(() => {
        const fetchSubscribedUsers = async () => {
            try {
                const response = await apiFetch(`${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/subscribed_users`);

                if (!response.ok) {
                    throw new Error('Failed to fetch subscribed users');
                }

                const data = await response.json();
                setSubscribedUsers(data);
            } catch (err) {
                setError(err.message);
            }
        };

        fetchSubscribedUsers();
    }, []);

    useEffect(() => {
        const fetchInactiveSubscriptions = async () => {
            try {
                const response = await apiFetch(`${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/unsubscribed_users`);

                if (!response.ok) {
                    throw new Error('Failed to fetch inactive subscriptions');
                }

                const data = await response.json();
                setInactiveSubscriptions(data);
            } catch (err) {
                setError(err.message);
            }
        };

        fetchInactiveSubscriptions();
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await apiFetch(`${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/invitation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error('Failed to send invitation');
            }

            // Clear form on success
            setFormData({
                email: '',
                firstName: '',
                lastName: '',
            });
            setError(null);
            setSuccess(true);

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccess(false);
            }, 3000);
        } catch (err) {
            setError(err.message);
            setSuccess(false);
        }
    };

    const handleSendInvitation = async (email, firstName, lastName) => {
        try {
            const response = await apiFetch(`${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/invitation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, firstName, lastName }),
            });

            if (!response.ok) {
                throw new Error('Failed to send invitation');
            }

            setInvitationStatus(prev => ({
                ...prev,
                [email]: { success: true, message: 'Invitation sent successfully!' }
            }));

        } catch (err) {
            setInvitationStatus(prev => ({
                ...prev,
                [email]: { success: false, message: err.message }
            }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "No data";
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className="dashboard-root">
            <div className="db-container">
                <div className="dashboard">
                    <div className="dashboard-container">
                        <div className="dashboard-content">
                            <h3>User Management</h3>
                            <p style={{ color: "#707070" }}>
                                Send invitations and manage user subscriptions.
                            </p>
                        </div>

                        <Paper className={classes.root}>
                            <Typography variant="h5" gutterBottom style={{ textAlign: 'left' }}>
                                Section 1: Send Invitations
                            </Typography>
                            <form className={classes.form} onSubmit={handleSubmit}>
                                <TextField
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    label="First Name"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    label="Last Name"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.adminButton}
                                    fullWidth
                                >
                                    Send Invitation
                                </Button>
                                {error && <Typography className={classes.error}>{error}</Typography>}
                                {success && (
                                    <Typography className={classes.success}>
                                        Invitation sent successfully!
                                    </Typography>
                                )}
                            </form>
                        </Paper>

                        <Paper className={classes.root}>
                            <Typography variant="h5" gutterBottom style={{ textAlign: 'left' }}>
                                Section 2: Active Subscriptions
                            </Typography>
                            <div className={classes.subscriptionsLink}>
                                <Button
                                    variant="contained"
                                    href="https://dashboard.stripe.com/subscriptions?status=active"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    fullWidth
                                    className={classes.adminButton}
                                >
                                    View All Subscriptions in Stripe
                                </Button>
                            </div>
                            <TableContainer className={classes.tableContainer}>
                                <Table className={classes.table} aria-label="subscribed users table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.tableHeader}>Email</TableCell>
                                            <TableCell className={classes.tableHeader}>First Name</TableCell>
                                            <TableCell className={classes.tableHeader}>Last Name</TableCell>
                                            <TableCell className={classes.tableHeader}>Date Joined</TableCell>
                                            <TableCell className={classes.tableHeader}>Subscription Expiration</TableCell>
                                            <TableCell className={classes.tableHeader}>Manage Subscription</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subscribedUsers.map((user) => (
                                            <TableRow key={user.email}>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>{user.first_name}</TableCell>
                                                <TableCell>{user.last_name}</TableCell>
                                                <TableCell>{formatDate(user.date_joined)}</TableCell>
                                                <TableCell>{formatDate(user.subscr_expires)}</TableCell>
                                                <TableCell>
                                                    <Link
                                                        href={`https://dashboard.stripe.com/subscriptions/${user.stripe_subscription_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Manage Subscription
                                                    </Link>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        <Paper className={classes.root}>
                            <Typography variant="h5" gutterBottom style={{ textAlign: 'left' }}>
                                Section 3: Inactive Subscriptions
                            </Typography>
                            <TableContainer className={classes.tableContainer}>
                                <Table className={classes.table} aria-label="inactive subscriptions table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.tableHeader}>Email</TableCell>
                                            <TableCell className={classes.tableHeader}>First Name</TableCell>
                                            <TableCell className={classes.tableHeader}>Last Name</TableCell>
                                            <TableCell className={classes.tableHeader}>Date Joined</TableCell>
                                            <TableCell className={classes.tableHeader}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {inactiveSubscriptions.map((user) => (
                                            <TableRow key={user.email}>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>{user.first_name}</TableCell>
                                                <TableCell>{user.last_name}</TableCell>
                                                <TableCell>{formatDate(user.date_joined)}</TableCell>
                                                <TableCell>
                                                    {invitationStatus[user.email] ? (
                                                        <Typography
                                                            className={invitationStatus[user.email].success ? classes.success : classes.error}
                                                        >
                                                            {invitationStatus[user.email].message}
                                                        </Typography>
                                                    ) : (
                                                        <Link
                                                            component="button"
                                                            variant="body2"
                                                            onClick={() => handleSendInvitation(user.email, user.first_name, user.last_name)}
                                                        >
                                                            Send Invitation
                                                        </Link>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminUserManagement;